import { Formik } from 'formik'
import { startCase } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { AutoComplete, WorkflowDateTimePicker as DateTimePicker, FilterSelect, Text } from '@/components/Form'

import messages from './messages'

import './index.scss'

import styled from 'styled-components'

import Field from '@/components/Form/Field'

const StyledDateTimePicker = styled(DateTimePicker)`
  .react-datepicker-wrapper {
    margin-bottom: 0;
    margin-left: 0;
    width: 100%;
  }
`
function AuditFilters(props: any) {
  const intl = useIntl()

  const [state, setState] = useState({
    searchTerm: ''
  })

  const isValidDateTime = (value: any) => {
    if (typeof value === 'string') {
      value = moment(value, moment.ISO_8601)
    }

    return value.isValid()
  }

  const isValidEndDateOption = (value: any) => {
    let startDisplayTimestamp = props.searchParams.oldest

    if (startDisplayTimestamp === '') {
      startDisplayTimestamp = moment().toISOString()
    } else if (!isValidDateTime(startDisplayTimestamp)) {
      return false
    }

    value = typeof value === 'string' ? moment(value, moment.ISO_8601) : value
    return value.isAfter(moment(startDisplayTimestamp, moment.ISO_8601).subtract(1, 'day'), 'day')
  }

  /**
   * Creates a search query for the audit logs
   *
   * @param searchParams
   * @returns {object} the query for the audit api
   */
  function constructQuery(searchParams: any) {
    return Object.keys(searchParams).reduce(
      (query, key) => {
        let value = searchParams[key]
        const prefix = query.query ? query.query + ' AND ' : ''
        if (key === 'message' && value.length > 0) {
          query.queryField = '*'
          query.query = `${prefix}(${value
            .map((value: any) => key + ':' + (value && value.trim() ? value : '*'))
            .join(' OR ')
            .concat(')')}`
        }
        if (key === 'eventDetails' && value) {
          if (['and', 'or'].includes(value.toLowerCase())) value = `("${value}")` // query_string will throw error if incorrect syntax
          query.queryField = '*'
          query.query = `${prefix}(*:"${value}")`
        } else if (!['query', 'message', 'eventDetails'].includes(key)) {
          query[key] = value
        }
        return query
      },
      {} as { query?: string; queryField?: string; [key: string]: any }
    )
  }

  const onChangeDateTime = (id: any, name: any, value: any) => {
    const searchParams = {
      ...props.searchParams,
      start: 0,
      [id]:
        typeof value === 'string'
          ? value // invalid date/time entered but let the user type anyway
          : value.toISOString() // moment object returned by react-datetime
    }
    const query = constructQuery(searchParams)
    props.searchLogs(searchParams, query)
  }
  const onUpdateValue = (id: any, element: any) => {
    const searchParams = {
      ...props.searchParams,
      start: 0,
      offset: 0,
      [id]: element && element.target ? element.target.value : element
    }
    const query = constructQuery(searchParams)
    props.searchLogs(searchParams, query)
  }
  const resetUserSearch = () => {
    const searchParams = {
      ...props.searchParams,
      actor: '',
      start: 0
    }
    const query = constructQuery(searchParams)
    props.searchLogs(searchParams, query)
  }
  const onSearchChange = (value: any) => {
    value = value.trim()

    setState({ searchTerm: value })
    // Don't search if the user enters in spaces
    if (value.length === 0) {
      resetUserSearch()
    }
    props.fetchUserSearchResults(value)
  }
  const onSearchSelect = (value: any) => {
    // Reset the input to the search box
    setState({ searchTerm: value })

    const searchParams = {
      ...props.searchParams,
      actor: value.value,
      start: 0
    }
    const query = constructQuery(searchParams)
    props.searchLogs(searchParams, query)

    return { display: value.value, value: value.value }
  }

  const renderItem = ({ label, leftIcon, subLabel, value }: any) => {
    return (
      <div key={value} className="audit-user-result">
        {!!leftIcon && <div className="result__left">{leftIcon}</div>}
        <div className="result__right">
          <p>{label}</p>
          {!!subLabel && <p>{subLabel}</p>}
        </div>
      </div>
    )
  }

  const { events, generateSearchResults, isSearching, searchParams, users } = props

  return (
    <Formik
      onSubmit={() => null}
      initialValues={{
        user: '',
        message: [],
        event: searchParams.action || '',
        notificationId: searchParams.notificationId || '',
        start: searchParams.oldest ? moment(searchParams.oldest).format('YYYY-MM-DD') : '',
        end: searchParams.latest ? moment(searchParams.latest).format('YYYY-MM-DD') : ''
      }}
    >
      <div className="audit-search-fields">
        <div className="flex flex-wrap">
          <FilterSelect
            label={<FormattedMessage {...messages.event} />}
            onChange={onUpdateValue.bind(null, 'message')}
            name="message"
            multiple
            closeOnChange
            options={events.map((event: any) => ({
              key: event.key,
              value: event.key,
              text: startCase(event.key)
            }))}
            noResultsMessage={intl.formatMessage(messages.noMatchingEvents)}
            placeholder={intl.formatMessage(messages.all)}
            className="fill-space"
          />
          <Text
            label={<FormattedMessage {...messages.eventDetails} />}
            name="eventDetails"
            placeholder={intl.formatMessage(messages.eventDetailsHolder)}
            onChange={onUpdateValue.bind(null, 'eventDetails')}
          />
        </div>
        <div className="flex flex-wrap">
          <div className="flex">
            <AutoComplete
              key="user"
              name="user"
              label={<FormattedMessage {...messages.user} />}
              isSearching={isSearching}
              placeholder={intl.formatMessage(messages.userPlaceholder)}
              onResultClick={onSearchSelect}
              onSearchChange={onSearchChange}
              renderResult={renderItem}
              results={generateSearchResults(state.searchTerm, users)}
            />
            <Text
              label={<FormattedMessage {...messages.parentNotificationId} />}
              name="notificationId"
              onChange={onUpdateValue.bind(null, 'notificationId')}
              placeholder={intl.formatMessage(messages.parentNotificationIdPlaceholder)}
            />
          </div>

          <div className="flex">
            <Field label="Date Range Start">
              <StyledDateTimePicker
                onChange={onChangeDateTime.bind(null, 'oldest')}
                label={<FormattedMessage {...messages.start} />}
                value={searchParams.oldest}
                name="start"
                showIcon={true}
                showTimeSelect
                timeIntervals={15}
              />
            </Field>
            <Field label="Date Range End">
              <StyledDateTimePicker
                onChange={onChangeDateTime.bind(null, 'latest')}
                isValid={isValidEndDateOption}
                label={<FormattedMessage {...messages.end} />}
                value={searchParams.latest}
                name="end"
                showIcon={true}
                showTimeSelect
                timeIntervals={15}
              />
            </Field>
          </div>
        </div>
      </div>
    </Formik>
  )
}

AuditFilters.displayName = 'AuditFilters'

AuditFilters.propTypes = {
  intl: PropTypes.any.isRequired,
  searchLogs: PropTypes.func.isRequired,
  fetchUserSearchResults: PropTypes.func.isRequired,
  generateSearchResults: PropTypes.func.isRequired,
  isSearching: PropTypes.bool,
  users: PropTypes.array.isRequired,
  searchParams: PropTypes.object.isRequired,
  events: PropTypes.array.isRequired
}

AuditFilters.defaultProps = {
  isSearching: false
}

export default AuditFilters
